import type { BiPromptProjection } from '../../../../supabase/functions/_shared/bi-v2/retrievalContext';
import { composeGovernedFrameworkContext } from '../../../../supabase/functions/_shared/governedFrameworkContext';
import type { DbFrameworkPack } from '../../../../supabase/functions/_shared/frameworkPackDb';
import { recordPracticeContext, type PracticeContext } from '../../../../supabase/functions/_shared/practiceSelectionContext';
import { unsafePersistedText } from '../../../../supabase/functions/_shared/governedPromptAssembly';
import { createPromptSelection, type PromptSelection } from "../../../../supabase/functions/_shared/contextAssemblyReceipt";
import type {
  CoachLensSource,
  CoachMemorySource,
  CoachTwinSource,
} from "@/lib/coachSessionReviewContract";
import { buildCoachVeeraIdentityContextFromSources } from "@/lib/behavior/coachVeeraIdentityContext";
import type {
  CoachSessionPrepAgreementRecord,
  CoachSessionPrepLatestGrowthRecord,
} from "@/hooks/useCoachSessionPrepData";
import type { MeaningfulNoticingCard } from "@/components/coach/sessionPrep/MeaningfulNoticingSection";
import type {
  LeadershipRecordItem,
  LeadershipRecordSourceType,
} from "@/lib/behavior/viewModels/buildLeadershipRecordItems";

export interface CoachVeeraPrepEvidenceRef {
  id: string;
  label: string;
  route: string;
  sourceRevision?: string;
}

export type NextSessionBriefVisibility =
  | "coach_only"
  | "coach_reviewed"
  | "leader_safe"
  | "not_shareable_yet";

export type NextSessionBriefConfidence = "high" | "medium" | "low";

export type VisibilityState = NextSessionBriefVisibility;
export type BriefConfidence = NextSessionBriefConfidence;

export interface NextSessionBrief {
  leaderId: string;
  generatedAt: string;
  sinceLastSession: {
    summary: string;
    evidenceCount: number;
    lastObservedAt?: string;
  };
  evidenceBackedMoments: Array<{
    id: string;
    title: string;
    observedFact: string;
    sourceLabel?: string;
    occurredAt?: string;
    confidence: NextSessionBriefConfidence;
    visibility: NextSessionBriefVisibility;
  }>;
  possibleCoachingEdge?: {
    label: string;
    rationale: string;
    confidence: NextSessionBriefConfidence;
    visibility: NextSessionBriefVisibility;
  };
  whatIsNotKnown: string[];
  suggestedCoachQuestions: string[];
  carryForward: {
    note: string;
    updatedAt?: string;
    visibility: "coach_only";
  };
  visibility: NextSessionBriefVisibility;
}

export interface CoachVeeraPrepClaimCard {
  id: string;
  connection: string;
  question: string;
  whyThisSurfaced: string;
  evidenceRefs: CoachVeeraPrepEvidenceRef[];
}

export interface CoachSessionPrepVeeraBrief {
  claimCards: CoachVeeraPrepClaimCard[];
  observedShifts: string[];
  questionsToExplore: string[];
  watchouts: string[];
  evidenceRefs: CoachVeeraPrepEvidenceRef[];
  primaryFocus: string;
  whatChanged: string[];
  practiceContext?:PracticeContext;
  whatPersists: string[];
  unresolvedTension: string | null;
  coachingMoves: string[];
  confidenceLabel: "low" | "medium" | "high";
  confidenceNote: string;
  nextSessionBrief: NextSessionBrief;
  coachContradictionProbes?:string[];
}

const GUARDED_COPY_PATTERN =
  /\b(nudge|score|rank|diagnose|diagnosis|risk|red flag|performance rating|readiness|coaching score|behavior assessment|surveillance|monitor|autonomous|digital twin)\b/i;

function dedupeStrings(values: Array<string | null | undefined>, limit = 6): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    const normalized = value?.trim();
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    result.push(normalized);
    if (result.length >= limit) break;
  }
  return result;
}

function dedupeEvidenceRefs(values: CoachVeeraPrepEvidenceRef[], limit = 6): CoachVeeraPrepEvidenceRef[] {
  const seen = new Set<string>();
  const result: CoachVeeraPrepEvidenceRef[] = [];
  for (const value of values) {
    const key = `${value.label}|${value.route}`;
    if (!value.label || seen.has(key)) continue;
    seen.add(key);
    result.push(value);
    if (result.length >= limit) break;
  }
  return result;
}

function stripTerminalPunctuation(value: string): string {
  return value.replace(/[.!?]+$/g, "").trim();
}

function lowerFirst(value: string): string {
  return value.length > 0 ? `${value[0].toLowerCase()}${value.slice(1)}` : value;
}

function toCoachQuestion(value: string): string | null {
  const normalized = value.replace(/\s+/g, " ").trim();
  if (!normalized || GUARDED_COPY_PATTERN.test(normalized)) return null;
  if (normalized.endsWith("?")) return normalized;

  const withoutPunctuation = stripTerminalPunctuation(normalized);
  const checkWhether = withoutPunctuation.match(/^check whether\s+(.+)$/i);
  if (checkWhether?.[1]) {
    return `What would show whether ${lowerFirst(checkWhether[1])}?`;
  }

  const practice = withoutPunctuation.match(/^practice\s+(.+)$/i);
  if (practice?.[1]) {
    return `What would make ${lowerFirst(practice[1])} useful to explore in the next conversation?`;
  }

  const use = withoutPunctuation.match(/^use\s+(.+)$/i);
  if (use?.[1]) {
    return `What would be useful to explore about ${lowerFirst(use[1])}?`;
  }

  return `What would be useful to explore about ${lowerFirst(withoutPunctuation)}?`;
}

function buildSuggestedCoachQuestions(values: string[]): string[] {
  const safeQuestions = dedupeStrings(values.map(toCoachQuestion), 5);
  return safeQuestions.length > 0
    ? safeQuestions
    : [
        "What example from the recent evidence would be most useful to explore first?",
        "What would the leader say is different since the last session?",
      ];
}

function buildWhatIsNotKnown(params: {
  evidenceRefs: CoachVeeraPrepEvidenceRef[];
  leadershipRecordItems?: LeadershipRecordItem[];
}): string[] {
  const hasDirectReflection = params.evidenceRefs.some((entry) =>
    /reflection/i.test(`${entry.label} ${entry.route}`),
  ) || (params.leadershipRecordItems || []).some((item) => item.sourceType === "reflection" || item.sourceType === "shared_summary");
  const recordUncertainty = (params.leadershipRecordItems || []).flatMap((item) => item.uncertainty || []);

  return dedupeStrings([
    ...recordUncertainty,
    "This may reflect meeting context rather than a durable pattern.",
    params.evidenceRefs.length < 2 && (params.leadershipRecordItems || []).length < 2
      ? "Available evidence is limited to recent meeting transcripts, reflections, or saved coach notes on this page."
      : null,
    hasDirectReflection ? null : "No direct leader reflection has been reviewed yet.",
    "Nothing here should become leader-visible without coach review.",
  ], 4);
}

const LEADERSHIP_RECORD_SOURCE_LABELS: Record<LeadershipRecordSourceType, string> = {
  bi_observation: "BI observation",
  context_graph_node: "Context graph node",
  leader_memory_event: "Leader memory event",
  practice_summary: "Practice summary",
  transcript_evidence: "Transcript evidence",
  reflection: "Reflection",
  coach_note: "Coach note",
  shared_summary: "Shared summary",
};

function buildEvidenceBackedMoments(params: {
  claimCards: CoachVeeraPrepClaimCard[];
  whatChanged: string[];
  evidenceRefs: CoachVeeraPrepEvidenceRef[];
  confidenceLabel: NextSessionBriefConfidence;
  leadershipRecordItems?: LeadershipRecordItem[];
  lastObservedAt?: string;
  selection?: PromptSelection;
}): NextSessionBrief["evidenceBackedMoments"] {
  const eligibleRecords = (params.leadershipRecordItems || [])
    .filter((item) => item.visibility === "coach_only" || item.visibility === "coach_reviewed");
  const selectedRecords = eligibleRecords.slice(0, 4);
  const recordMoments = selectedRecords
    .map((item) => ({
      id: item.id,
      title: item.title,
      observedFact: item.observedFact,
      sourceLabel: LEADERSHIP_RECORD_SOURCE_LABELS[item.sourceType],
      occurredAt: item.occurredAt || item.createdAt,
      confidence: item.confidence || params.confidenceLabel,
      visibility: "coach_only" as const,
    }));
  if (recordMoments.length > 0) {
    params.selection?.cap("prep_brief_moments", eligibleRecords.length, 4);
    for (const item of selectedRecords) {
      // sourceId is canonical lineage; item.id is a presentation key.
      if (typeof item.sourceId === "string" && /^[A-Za-z0-9][A-Za-z0-9:_-]{0,127}$/.test(item.sourceId)) {
        params.selection?.ids("prep_brief_moments", [{ source_id: item.sourceId, visibility_scope: "coach_only" }]);
      } else {
        params.selection?.unattributed("prep_brief_moments", 1);
      }
    }
    return recordMoments;
  }

  const claimMoments = params.claimCards.slice(0, 4).map((card, index) => {
    const source = card.evidenceRefs[0];
    return {
      id: card.id || `moment-${index + 1}`,
      title: source?.label || `Observable moment ${index + 1}`,
      observedFact: card.connection,
      sourceLabel: source?.label,
      occurredAt: params.lastObservedAt,
      confidence: params.confidenceLabel,
      visibility: "coach_only" as const,
    };
  });
  if (claimMoments.length > 0) {
    params.selection?.cap("prep_brief_moments", params.claimCards.length, 4);
    params.selection?.unattributed("prep_brief_moments", claimMoments.length);
    return claimMoments;
  }

  const fallbackFact = params.whatChanged[0];
  if (fallbackFact) {
    params.selection?.unattributed("prep_brief_moments", 1);
    const source = params.evidenceRefs[0];
    return [{
      id: source?.id || "available-prep-evidence",
      title: source?.label || "Available prep evidence",
      observedFact: fallbackFact,
      sourceLabel: source?.label,
      occurredAt: params.lastObservedAt,
      confidence: params.confidenceLabel,
      visibility: "coach_only",
    }];
  }

  params.selection?.unattributed("prep_brief_moments", 1);
  return [{
    id: "thin-evidence",
    title: "Evidence still thin",
    observedFact: "Available evidence is thin, so this brief should be treated as a coach-review starting point.",
    confidence: "low",
    visibility: "coach_only",
  }];
}

function buildNextSessionBrief(params: {
  leaderId?: string;
  generatedAt?: string;
  primaryFocus: string;
  whatChanged: string[];
  whatPersists: string[];
  questionsToExplore: string[];
  latestCoachGrowthRecord?: CoachSessionPrepLatestGrowthRecord | null;
  claimCards: CoachVeeraPrepClaimCard[];
  evidenceRefs: CoachVeeraPrepEvidenceRef[];
  leadershipRecordItems?: LeadershipRecordItem[];
  confidenceLabel: NextSessionBriefConfidence;
  selection?: PromptSelection;
}): NextSessionBrief {
  const recordItems = params.leadershipRecordItems || [];
  const lastObservedAt = params.latestCoachGrowthRecord?.created_at
    || recordItems[0]?.occurredAt
    || recordItems[0]?.createdAt;
  const evidenceCount = recordItems.length > 0 ? recordItems.length : params.evidenceRefs.length;
  const carryForwardNote = params.whatPersists[0];
  const evidenceBackedMoments = buildEvidenceBackedMoments({
    claimCards: params.claimCards,
    whatChanged: params.whatChanged,
    evidenceRefs: params.evidenceRefs,
    leadershipRecordItems: recordItems,
    confidenceLabel: params.confidenceLabel,
    lastObservedAt,
    selection: params.selection,
  });
  const primaryRecordFact = recordItems[0]?.observedFact;

  return {
    leaderId: params.leaderId || "unknown",
    generatedAt: params.generatedAt || new Date().toISOString(),
    sinceLastSession: {
      summary: primaryRecordFact || params.primaryFocus || "Available evidence is thin; use this as a coach-review starting point.",
      evidenceCount,
      lastObservedAt,
    },
    evidenceBackedMoments,
    possibleCoachingEdge: evidenceCount > 0
      ? {
          label: "A possible area to explore",
          rationale: `This surfaced from ${evidenceCount} evidence reference${evidenceCount === 1 ? "" : "s"} and should be reviewed by the coach before it is treated as durable.`,
          confidence: params.confidenceLabel,
          visibility: "coach_only",
        }
      : undefined,
    whatIsNotKnown: buildWhatIsNotKnown({
      evidenceRefs: params.evidenceRefs,
      leadershipRecordItems: recordItems,
    }),
    suggestedCoachQuestions: buildSuggestedCoachQuestions([
      ...params.questionsToExplore,
      ...(params.latestCoachGrowthRecord?.nextConversationSignals || []),
      ...(params.latestCoachGrowthRecord?.direction || []),
      params.primaryFocus,
    ]),
    carryForward: {
      note: carryForwardNote || "",
      visibility: "coach_only",
      ...(carryForwardNote && lastObservedAt ? { updatedAt: lastObservedAt } : {}),
    },
    visibility: "coach_only",
  };
}

function toClaimCard(card: MeaningfulNoticingCard): CoachVeeraPrepClaimCard {
  return {
    id: `fallback-${card.id}`,
    connection: card.notice,
    question: card.whatToExplore,
    whyThisSurfaced: card.whyItMayMatter,
    evidenceRefs: dedupeEvidenceRefs(
      card.evidence.map((entry, idx) => ({
        id: entry.evidenceId || `${card.id}-${idx}`,
        label: entry.label,
        route: entry.link || "",
      })),
      4,
    ),
  };
}

function buildRuntimeWatchouts(params: {
  coachLens?: CoachLensSource | null;
  coachTwin?: CoachTwinSource | null;
  coachMemory?: CoachMemorySource | null;
  frameworkWatchout?: string | null;
}): string[] {
  const philosophy = params.coachLens?.coach_lens_json?.philosophy?.trim();
  const avoidLanguage = (params.coachLens?.avoid_language || []).filter(Boolean).slice(0, 2);
  const guardrails = (params.coachTwin?.guardrails || []).filter(Boolean).slice(0, 2);
  const avoidLanguageCount = (params.coachLens?.avoid_language || [])
    .filter((value) => value && !GUARDED_COPY_PATTERN.test(value))
    .slice(0, 2)
    .length;
  const recentThemes = (params.coachMemory?.recentNodes || [])
    .flatMap((node) => [node.title, node.content])
    .filter((value): value is string => typeof value === "string" && value.trim().length > 0)
    .map((value) => value.trim())
    .slice(0, 2);

  return dedupeStrings([
    guardrails[0],
    guardrails[1],
    params.frameworkWatchout,
    avoidLanguageCount > 0 ? "Avoid defaulting to unsupported labels if the evidence does not support them." : null,
    philosophy ? `Keep the brief aligned with the coach lens: ${philosophy}` : null,
    recentThemes[0] ? `Recent coach memory theme to carry forward: ${recentThemes[0]}` : null,
  ], 4);
}

export function buildCoachSessionPrepVeeraBrief(params: {
  leaderId?: string;
  generatedAt?: string;
  meaningfulNoticingCards: MeaningfulNoticingCard[];
  governedProjection?:BiPromptProjection;
  governedProofDigest?:string|null;
  governedPriority?:Parameters<typeof composeGovernedFrameworkContext>[0]['priority'];
  frameworkPack?:DbFrameworkPack|null;
  frameworkContextStatus?:'ready'|'unavailable';
  practiceContext?:PracticeContext;
  agreementRecord?: CoachSessionPrepAgreementRecord | null;
  latestCoachGrowthRecord?: CoachSessionPrepLatestGrowthRecord | null;
  coachLens?: CoachLensSource | null;
  coachTwin?: CoachTwinSource | null;
  coachMemory?: CoachMemorySource | null;
  leadershipRecordItems?: LeadershipRecordItem[];
  selection?: PromptSelection;
  coachContradictionProbes?:string[];
}): CoachSessionPrepVeeraBrief {
  const selection = params.selection ?? createPromptSelection();
  const practices=recordPracticeContext(params.practiceContext??{status:'empty',items:[]},selection);
  if(params.governedProjection){
    const context=composeGovernedFrameworkContext({projection:params.governedProjection,audience:'coach',
      proofDigest:params.governedProofDigest,pack:params.frameworkPack,priority:params.governedPriority,includeWithheldSlots:false,selection,maxThemes:4,maxMoments:4,maxCharacters:10000});
    const refBySource=new Map<string,CoachVeeraPrepEvidenceRef>();
    const refs:CoachVeeraPrepEvidenceRef[]=[];
    const refByMoment=new Map(context.moments.map(moment=>{
      const key=`${moment.source_type}:${moment.source_id}`;
      let ref=refBySource.get(key);
      if(!ref){
        ref={sourceRevision:moment.source_revision,id:`${moment.source_type==='transcript_upload'?'transcript':'reflection'}-${moment.source_id}`,
          label:`${moment.source_type==='transcript_upload'?'Transcript':'Reflection'} source ${refs.length+1}`,
          route:moment.source_type==='transcript_upload'?`/coach/post-meeting/${encodeURIComponent(moment.source_id)}`
            :`/coach/leaders/${encodeURIComponent(params.leaderId??'unknown')}?section=evidence&type=reflection&id=${encodeURIComponent(moment.source_id)}`};
        refBySource.set(key,ref);refs.push(ref);
      }
      return [moment.id,ref] as const;
    }));
    const sourceLabels=context.moments.map(moment=>refByMoment.get(moment.id)!.label);
    const moments=context.moments.map((moment,index)=>({id:moment.id,title:moment.evidence_kind==='reported'?'Leader reported moment':'Observed moment',
      observedFact:moment.quote,sourceLabel:sourceLabels[index],confidence:'low' as const,visibility:'coach_only' as const}));
    // Show every admitted interpretation in full; never shorten a governing replacement.
    const readings=context.interpretation.items.map(item=>item.text);
    const questions=context.interpretation.items.map(item=>item.question??`What current evidence would help you explore this reading: ${item.text}`);
    const primaryFocus=readings[0]??context.themes[0]?.label??'Explore the current evidence without assuming a settled pattern.';
    const claims=context.interpretation.items.map(item=>{
      const theme=context.themes.find(theme=>theme.theme_id===item.target_id);
      const ids=item.slot_id==='moment_interpretation'?[item.target_id]:theme?[...theme.supporting_moment_ids,...theme.contrary_moment_ids]:[];
      const claimRefs=dedupeEvidenceRefs(ids.flatMap(id=>refByMoment.has(id)?[refByMoment.get(id)!]:[]),ids.length);
      return {id:item.target_id,connection:item.text,question:item.question??'What would help test this reading?',
        whyThisSurfaced:claimRefs.length?'Current governed interpretation; validate against the cited moments.':'Current governing correction; source relations are pending.',evidenceRefs:claimRefs};
    });
    return {coachContradictionProbes:context.themes.filter(theme=>theme.contrary_moment_ids.length&&theme.label).map(theme=>`What evidence would challenge the current reading of ${theme.label}?`).filter(text=>!unsafePersistedText(text)).slice(0,4),claimCards:claims,observedShifts:[],questionsToExplore:questions,watchouts:['Frameworks are tentative framing. Reported experience is not observed behavior. Suggestions are not commitments.'],
      practiceContext:practices,evidenceRefs:refs,primaryFocus,whatChanged:[],whatPersists:context.themes.filter(theme=>theme.status==='active'&&Object.values(theme.supporting_situation_counts_by_evidence_kind??{}).some(count=>typeof count==='number'&&count>=2)).map(theme=>`Tentative theme: ${theme.label??theme.description??'Current corrected interpretation'}`),
      unresolvedTension:null,coachingMoves:questions,confidenceLabel:'low',confidenceNote:params.frameworkContextStatus==='unavailable'?'Current interpretation could not load. Retry coach context.':'Current governed evidence; validate meaning in conversation.',
      nextSessionBrief:{leaderId:params.leaderId??'unknown',generatedAt:params.generatedAt??new Date().toISOString(),
        sinceLastSession:{summary:primaryFocus,evidenceCount:refs.length},evidenceBackedMoments:moments,
        ...(readings.length?{possibleCoachingEdge:{label:readings[0],rationale:readings.join('\n'),confidence:'low' as const,visibility:'coach_only' as const}}:{}),
        whatIsNotKnown:['Attempts and commitment require their own native evidence.'],suggestedCoachQuestions:questions,
        carryForward:{note:'',visibility:'coach_only'},visibility:'coach_only'}};
  }
  const claimCards = params.meaningfulNoticingCards.slice(0, 5).map(toClaimCard);
  const evidenceRefs = dedupeEvidenceRefs(claimCards.flatMap((card) => card.evidenceRefs), 6);
  const identityContext = buildCoachVeeraIdentityContextFromSources({
    coachLens: params.coachLens,
    coachTwin: params.coachTwin,
  });

  const lensName = params.coachLens?.lens_name?.trim();
  const frameworks = (params.coachLens?.frameworks || [])
    .map((item) => item?.name?.trim())
    .filter((value): value is string => Boolean(value))
    .slice(0, 2);

  const observedShifts = dedupeStrings([
    ...(params.latestCoachGrowthRecord?.workedOn || []),
    ...(params.agreementRecord?.namedAgreements || []),
    ...params.meaningfulNoticingCards.map((card) => card.notice),
  ], 5);

  const whatChanged = dedupeStrings([
    ...(params.latestCoachGrowthRecord?.workedOn || []),
    ...params.meaningfulNoticingCards.map((card) => card.notice),
  ], 4);

  const historicalWhatPersists = dedupeStrings([
    ...(params.agreementRecord?.namedAgreements || []),
    ...(params.coachMemory?.recentNodes || [])
      .map((node) => node.title?.trim() || node.content?.trim() || "")
      .filter(Boolean),
  ], 4);

  // Native choices retain their own state and revision even before BI exists.
  const whatPersists = historicalWhatPersists;

  const questionsToExplore = dedupeStrings([
    ...(params.latestCoachGrowthRecord?.direction || []),
    ...(params.latestCoachGrowthRecord?.nextConversationSignals || []),
    ...params.meaningfulNoticingCards.map((card) => card.whatToExplore),
    identityContext.frameworkNotices[0],
    lensName ? `How does this pattern look through the ${lensName} lens?` : null,
    frameworks[0] ? `Which part of ${frameworks[0]} is most relevant in the next conversation?` : null,
  ], 6);

  const watchouts = dedupeStrings([
    ...buildRuntimeWatchouts({
      coachLens: params.coachLens,
      coachTwin: params.coachTwin,
      coachMemory: params.coachMemory,
      frameworkWatchout: identityContext.frameworkWatchouts[0] || null,
    }),
    "Avoid treating early patterns as fixed traits; test with fresh evidence in-session.",
  ], 4);

  const primaryFocus = dedupeStrings([
    params.meaningfulNoticingCards[0]?.notice,
    params.latestCoachGrowthRecord?.workedOn?.[0],
    params.agreementRecord?.namedAgreements?.[0],
    questionsToExplore[0],
    "Use the next conversation to validate the strongest live pattern.",
  ], 1)[0];

  const coachingMoves = dedupeStrings([
    ...(params.latestCoachGrowthRecord?.direction || []),
    ...(params.latestCoachGrowthRecord?.nextConversationSignals || []),
    ...params.meaningfulNoticingCards.map((card) => card.whatToExplore),
    lensName ? `Use the ${lensName} lens to pressure-test the next move.` : null,
    frameworks[0] ? `Pull one concrete move from ${frameworks[0]} into the session.` : null,
    "Name the strongest pattern and test it live with one focused question.",
  ], 4);

  const unresolvedTension = dedupeStrings([
    params.meaningfulNoticingCards[0]?.whatToExplore,
    claimCards[0]?.question,
    params.latestCoachGrowthRecord?.nextConversationSignals?.[0],
    null,
  ], 1)[0] ?? null;

  let confidenceLabel: CoachSessionPrepVeeraBrief["confidenceLabel"] = "low";
  if (claimCards.length >= 2 && evidenceRefs.length >= 2) {
    confidenceLabel = "high";
  } else if (claimCards.length >= 1 && evidenceRefs.length >= 1) {
    confidenceLabel = "medium";
  }

  const confidenceNote = confidenceLabel === "high"
    ? "Built from multiple recent evidence threads and should hold unless new contradictory evidence shows up live."
    : confidenceLabel === "medium"
      ? "Built from a recent evidence thread with moderate confidence. Validate it live before treating it as settled."
      : "Built from limited prep evidence, so treat this as an early signal to test rather than a settled read.";

  const nextSessionBrief = buildNextSessionBrief({
    leaderId: params.leaderId,
    generatedAt: params.generatedAt,
    primaryFocus,
    whatChanged,
    whatPersists: historicalWhatPersists,
    questionsToExplore,
    latestCoachGrowthRecord: params.latestCoachGrowthRecord,
    claimCards,
    evidenceRefs,
    leadershipRecordItems: params.leadershipRecordItems,
    confidenceLabel,
    selection,
  });

  // These shown sections aggregate intermediate facts without row-level lineage.
  selection.unattributed("prep_brief_summary", 1);
  selection.unattributed("prep_brief_edge", nextSessionBrief.possibleCoachingEdge ? 1 : 0);
  selection.unattributed("prep_brief_unknowns", nextSessionBrief.whatIsNotKnown.length);
  selection.unattributed("prep_brief_questions", nextSessionBrief.suggestedCoachQuestions.length);
  // Saved/reviewed carry-forward state is supplied by the page, outside this builder.
  return {
    claimCards,
    observedShifts,
    questionsToExplore,
    watchouts,
    evidenceRefs,
    primaryFocus,
    whatChanged,
    practiceContext:practices,
    whatPersists,
    unresolvedTension,
    coachingMoves,
    confidenceLabel,
    confidenceNote,
    nextSessionBrief,
    ...(params.coachContradictionProbes?.length ? {coachContradictionProbes:params.coachContradictionProbes} : {}),
  };
}
